import * as yup from 'yup';

export const formPassSchema = yup.object().shape({
  reason: yup.string().required(),
  notes: yup.string().optional(),
});

export type FormState = yup.InferType<typeof formPassSchema>;

export type PassRequestReason = 'unavailable' | 'nte-too-low' | 'description-unclear' | 'other';

export const PASS_REQUEST_SCREEN_KEYS = {
  SELECT_REASON: 'select-reason',
  UNAVAILABLE: 'unavailable',
  NTE_TO_LOW: 'nte-too-low',
  DESCRIPTION_UNCLEAR: 'description-unclear',
  OTHER: 'other',
  PASS_SUCCESS: 'pass-success',
} as const;

export type PassRequestKey =
  (typeof PASS_REQUEST_SCREEN_KEYS)[keyof typeof PASS_REQUEST_SCREEN_KEYS];

export type PassRequestReasonOption = {
  label: string;
  value: PassRequestReason;
};

export const PASS_REASONS_LIST: PassRequestReasonOption[] = [
  {
    label: 'Unavailable for proposed times',
    value: PASS_REQUEST_SCREEN_KEYS.UNAVAILABLE,
  },
  {
    label: 'The NTE is too low',
    value: PASS_REQUEST_SCREEN_KEYS.NTE_TO_LOW,
  },
  {
    label: "Issue description doesn't make sense",
    value: PASS_REQUEST_SCREEN_KEYS.DESCRIPTION_UNCLEAR,
  },
  {
    label: 'Other',
    value: PASS_REQUEST_SCREEN_KEYS.OTHER,
  },
];

export type PassRequestScreenData = {
  TITLE: string;
  DESCRIPTION: string;
  NOTES_LABEL: string;
  NOTES_REQUIRED: boolean;
  NOTES_PLACEHOLDER: string;
  HIDE_SUBMIT: boolean;
  SUBMIT_LABEL: string;
  SUBMIT_STYLE: string;
  HIDE_BACK: boolean;
  BACK_LABEL: string;
  BACK_STYLE: string;
  HIDE_HEADER: boolean;
  HIDE_FOOTER: boolean;
};

export const PASS_REQUEST_TAB_DATA: Record<PassRequestKey, PassRequestScreenData> = {
  [PASS_REQUEST_SCREEN_KEYS.SELECT_REASON]: {
    TITLE: 'Select Reason',
    DESCRIPTION: 'Choose the reason you’re passing on this work order.',
    NOTES_LABEL: '',
    NOTES_REQUIRED: false,
    NOTES_PLACEHOLDER: '',
    HIDE_SUBMIT: false,
    SUBMIT_LABEL: 'Next',
    SUBMIT_STYLE: 'has-primary',
    HIDE_BACK: false,
    BACK_LABEL: 'Back',
    BACK_STYLE: 'has-secondary',
    HIDE_HEADER: false,
    HIDE_FOOTER: false,
  },
  [PASS_REQUEST_SCREEN_KEYS.UNAVAILABLE]: {
    TITLE: 'Unavailable for Proposed Times',
    DESCRIPTION: 'You can suggest new times or pass on this work order.',
    NOTES_LABEL: 'Additional Notes',
    NOTES_REQUIRED: false,
    NOTES_PLACEHOLDER: 'Tell us why the times don’t work.',
    HIDE_SUBMIT: false,
    SUBMIT_LABEL: 'Pass Work Order',
    SUBMIT_STYLE: 'has-danger',
    HIDE_BACK: false,
    BACK_LABEL: 'Back',
    BACK_STYLE: 'has-secondary',
    HIDE_HEADER: false,
    HIDE_FOOTER: false,
  },
  [PASS_REQUEST_SCREEN_KEYS.NTE_TO_LOW]: {
    TITLE: 'NTE Amount Too Low',
    DESCRIPTION: 'Tell us why the approved amount is not enough.',
    NOTES_LABEL: 'Notes',
    NOTES_REQUIRED: true,
    NOTES_PLACEHOLDER: 'Explain why more budget is needed.',
    HIDE_SUBMIT: false,
    SUBMIT_LABEL: 'Pass Work Order',
    SUBMIT_STYLE: 'has-danger',
    HIDE_BACK: false,
    BACK_LABEL: 'Back',
    BACK_STYLE: 'has-secondary',
    HIDE_HEADER: false,
    HIDE_FOOTER: false,
  },
  [PASS_REQUEST_SCREEN_KEYS.DESCRIPTION_UNCLEAR]: {
    TITLE: 'Issue Description Unclear',
    DESCRIPTION: 'Tell us what’s confusing about the request.',
    NOTES_LABEL: 'Notes',
    NOTES_REQUIRED: true,
    NOTES_PLACEHOLDER: 'Explain what’s unclear about the issue.',
    HIDE_SUBMIT: false,
    SUBMIT_LABEL: 'Pass Work Order',
    SUBMIT_STYLE: 'has-danger',
    HIDE_BACK: false,
    BACK_LABEL: 'Back',
    BACK_STYLE: 'has-secondary',
    HIDE_HEADER: false,
    HIDE_FOOTER: false,
  },
  [PASS_REQUEST_SCREEN_KEYS.OTHER]: {
    TITLE: 'Other Reason',
    DESCRIPTION: 'Tell us why you can’t take this work order.',
    NOTES_LABEL: 'Notes',
    NOTES_REQUIRED: true,
    NOTES_PLACEHOLDER: 'Provide a brief explanation.',
    HIDE_SUBMIT: false,
    SUBMIT_LABEL: 'Pass Work Order',
    SUBMIT_STYLE: 'has-danger',
    HIDE_BACK: false,
    BACK_LABEL: 'Back',
    BACK_STYLE: 'has-secondary',
    HIDE_HEADER: false,
    HIDE_FOOTER: false,
  },
  [PASS_REQUEST_SCREEN_KEYS.PASS_SUCCESS]: {
    TITLE: 'Work Order Passed',
    DESCRIPTION: 'This work order has been removed from your requests list.',
    NOTES_LABEL: '',
    NOTES_REQUIRED: false,
    NOTES_PLACEHOLDER: '',
    HIDE_SUBMIT: false,
    SUBMIT_LABEL: '',
    SUBMIT_STYLE: '',
    HIDE_BACK: false,
    BACK_LABEL: 'Return to Requests',
    BACK_STYLE: 'has-secondary',
    HIDE_HEADER: true,
    HIDE_FOOTER: false,
  },
};
